import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { listBiddingSpotMarketPrices } from '@ebalance/proto/bffuser/v1/bidding_spot_market_service-BiddingSpotMarketService_connectquery'
import { ListBiddingSpotMarketPricesRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_spot_market_service_pb'

import type { KomaRow } from '~/constants/spotPrice'
import { civilDateToProtoDate } from '~/models/civilDate'
import { toKomaRows } from '~/pages/SpotPrice/models/komaRows'
import type { SpotPricePeriod } from '~/pages/SpotPrice/models/spotPricePeriod'

type Result = {
  /** 受渡日の昇順・コマ番号の昇順。 取得前・取得失敗の間は空。 */
  rows: KomaRow[]
  /** 取得中 (初回)。 取得しない間 (period = null) は false。 */
  isPending: boolean
  /** 取得に失敗した (価格が出ない理由を画面に出すため)。 */
  isError: boolean
}

function ymdToProtoDate(ymd: string): ReturnType<typeof civilDateToProtoDate> {
  const [year, month, day] = ymd.split('-').map(Number)
  return civilDateToProtoDate({ year, month, day })
}

/**
 * BFF `BiddingSpotMarketService.ListBiddingSpotMarketPrices` で、 受渡日の期間の
 * スポット約定価格 (システムプライス・9 エリアのエリアプライス) を取得する hook
 * (`docs/specs/bidding/spot-bidding.md` §5、 AC-BSP-039 / AC-BSP-046)。
 *
 * - 期間はサーバー側で読む (request の `delivery_date_from` / `delivery_date_to`)。
 *   31 日以内に収めるのは呼び出し側 (`resolveSpotPricePeriod`)。
 * - **全画面エラー化を避けるため `throwOnError: false` を明示する**。 global
 *   QueryClient の既定は `throwOnError: true` で、 何も指定しないと取得失敗が router の
 *   errorElement (= 全画面エラー) へ飛び、 期間欄も触れなくなる。 失敗は画面内の
 *   Alert で知らせる。 retry は `ApiClientProvider` の `shouldRetryQuery` /
 *   `queryRetryDelay` に委譲する (一過性エラーだけ有限回リトライし、 その間は
 *   `isPending` のまま)。 前例: `pages/JepxSettlement/hooks/useListJepxSettlements.ts`。
 */
export function useListSpotMarketPrices(
  period: SpotPricePeriod | null,
): Result {
  const request = create(ListBiddingSpotMarketPricesRequestSchema, {
    deliveryDateFrom: period !== null ? ymdToProtoDate(period.from) : undefined,
    deliveryDateTo: period !== null ? ymdToProtoDate(period.to) : undefined,
  })
  const { data, isPending, isError } = useQuery(
    listBiddingSpotMarketPrices,
    request,
    { enabled: period !== null, throwOnError: false },
  )
  const rows = useMemo(() => toKomaRows(data?.komaPrices ?? []), [data])
  return { rows, isPending: period !== null && isPending, isError }
}
